import React, { useState } from 'react';

export type SubscriptionStatus =
  | 'active'
  | 'canceled'
  | 'past_due'
  | 'unpaid'
  | 'incomplete';

export interface Subscription {
  id: string;
  planId: string;
  planName: string;
  status: SubscriptionStatus;
  currentPeriodStart: Date;
  currentPeriodEnd: Date;
  cancelAtPeriodEnd: boolean;
  amount: number;
  currency: string;
  interval: 'month' | 'year';
}

export type SubscriptionAction = 'upgrade' | 'downgrade' | 'pause' | 'resume';

export interface SubscriptionManagerProps {
  subscription: Subscription;
  onSubscriptionChange: (action: SubscriptionAction) => void;
  onCancel: () => void;
  /** Enable dark mode colors (default: false for backward compat) */
  darkMode?: boolean;
  className?: string;
}

export const SubscriptionManager: React.FC<SubscriptionManagerProps> = ({
  subscription,
  onSubscriptionChange,
  onCancel,
  darkMode = false,
  className = '',
}) => {
  const [loading, setLoading] = useState(false);

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date);
  };

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const getStatusColor = (status: SubscriptionStatus) => {
    switch (status) {
      case 'active':
        return '#10b981';
      case 'canceled':
        return '#ef4444';
      case 'past_due':
        return '#f59e0b';
      case 'unpaid':
        return '#ef4444';
      case 'incomplete':
        return '#6b7280';
      default:
        return '#6b7280';
    }
  };

  const getStatusText = (status: SubscriptionStatus) => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'canceled':
        return 'Canceled';
      case 'past_due':
        return 'Past Due';
      case 'unpaid':
        return 'Unpaid';
      case 'incomplete':
        return 'Incomplete';
      default:
        return status;
    }
  };

  const handleAction = async (action: SubscriptionAction) => {
    setLoading(true);
    try {
      await onSubscriptionChange(action);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    setLoading(true);
    try {
      await onCancel();
    } finally {
      setLoading(false);
    }
  };

  // Color palette swaps for dark mode
  const bg = darkMode ? '#1f2937' : 'white';
  const border = darkMode ? '#374151' : '#e5e7eb';
  const text = darkMode ? '#f9fafb' : '#1f2937';
  const textMuted = darkMode ? '#9ca3af' : '#6b7280';
  const accent = darkMode ? '#60a5fa' : '#3b82f6';
  const mutedBg = darkMode ? '#111827' : '#f9fafb';
  const warningBg = darkMode ? '#78350f' : '#fef3c7';
  const warningText = darkMode ? '#fde68a' : '#92400e';
  const secondaryBg = darkMode ? '#374151' : '#f3f4f6';
  const secondaryText = darkMode ? '#d1d5db' : '#374151';
  const secondaryBorder = darkMode ? '#4b5563' : '#d1d5db';

  const styles = {
    container: {
      width: '100%',
    } as React.CSSProperties,
    card: {
      border: `1px solid ${border}`,
      borderRadius: '12px',
      padding: '2rem',
      background: bg,
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    } as React.CSSProperties,
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '1.5rem',
    } as React.CSSProperties,
    info: {
      flex: 1,
    } as React.CSSProperties,
    planName: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: text,
      margin: '0 0 0.5rem 0',
    } as React.CSSProperties,
    details: {
      display: 'flex',
      alignItems: 'baseline',
      gap: '0.25rem',
    } as React.CSSProperties,
    amount: {
      fontSize: '1.25rem',
      fontWeight: 600,
      color: accent,
    } as React.CSSProperties,
    interval: {
      fontSize: '1rem',
      color: textMuted,
    } as React.CSSProperties,
    statusBadge: {
      padding: '0.5rem 1rem',
      borderRadius: '20px',
      color: 'white',
      fontSize: '0.875rem',
      fontWeight: 600,
    } as React.CSSProperties,
    dates: {
      marginBottom: '2rem',
      padding: '1rem',
      background: mutedBg,
      borderRadius: '8px',
    } as React.CSSProperties,
    dateLabel: {
      fontSize: '0.875rem',
      color: textMuted,
      fontWeight: 500,
    } as React.CSSProperties,
    dateValue: {
      fontSize: '0.875rem',
      color: text,
    } as React.CSSProperties,
    cancellationNotice: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      marginTop: '1rem',
      padding: '0.75rem',
      background: warningBg,
      borderRadius: '6px',
      fontSize: '0.875rem',
      color: warningText,
    } as React.CSSProperties,
    actions: {
      display: 'flex',
      gap: '1rem',
      flexWrap: 'wrap' as const,
    } as React.CSSProperties,
    button: {
      padding: '0.75rem 1.5rem',
      border: 'none',
      borderRadius: '8px',
      fontWeight: 600,
      fontSize: '0.875rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      flex: 1,
      minWidth: '120px',
      minHeight: '44px',
    } as React.CSSProperties,
    buttonPrimary: {
      background: accent,
      color: 'white',
    } as React.CSSProperties,
    buttonSecondary: {
      background: secondaryBg,
      color: secondaryText,
      border: `1px solid ${secondaryBorder}`,
    } as React.CSSProperties,
    buttonDanger: {
      background: '#ef4444',
      color: 'white',
    } as React.CSSProperties,
    buttonDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed',
    } as React.CSSProperties,
  };

  return (
    <div
      style={styles.container}
      className={className}
      role="region"
      aria-label="Subscription management"
    >
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.info}>
            <h3 style={styles.planName} id="subscription-plan-name">
              {subscription.planName}
            </h3>
            <div style={styles.details}>
              <span style={styles.amount}>
                {formatAmount(subscription.amount, subscription.currency)}
              </span>
              <span style={styles.interval}>/{subscription.interval}</span>
            </div>
          </div>
          <div>
            <span
              style={{
                ...styles.statusBadge,
                backgroundColor: getStatusColor(subscription.status),
              }}
              role="status"
              aria-label={`Subscription status: ${getStatusText(subscription.status)}`}
            >
              {getStatusText(subscription.status)}
            </span>
          </div>
        </div>

        <div style={styles.dates}>
          <div>
            <div style={styles.dateLabel}>Current period:</div>
            <div style={styles.dateValue}>
              {formatDate(subscription.currentPeriodStart)} -{' '}
              {formatDate(subscription.currentPeriodEnd)}
            </div>
          </div>
          {subscription.cancelAtPeriodEnd && (
            <div style={styles.cancellationNotice} role="alert">
              <span aria-hidden="true">⚠️</span>
              <span>
                Your subscription will be canceled at the end of the current
                period.
              </span>
            </div>
          )}
        </div>

        <div style={styles.actions}>
          {subscription.status === 'active' &&
            !subscription.cancelAtPeriodEnd && (
              <>
                <button
                  style={{
                    ...styles.button,
                    ...styles.buttonSecondary,
                    ...(loading ? styles.buttonDisabled : {}),
                  }}
                  onClick={() => handleAction('upgrade')}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Upgrade Plan'}
                </button>
                <button
                  style={{
                    ...styles.button,
                    ...styles.buttonSecondary,
                    ...(loading ? styles.buttonDisabled : {}),
                  }}
                  onClick={() => handleAction('downgrade')}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Downgrade Plan'}
                </button>
                <button
                  style={{
                    ...styles.button,
                    ...styles.buttonDanger,
                    ...(loading ? styles.buttonDisabled : {}),
                  }}
                  onClick={handleCancel}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Cancel Subscription'}
                </button>
              </>
            )}

          {subscription.status === 'active' &&
            subscription.cancelAtPeriodEnd && (
              <button
                style={{
                  ...styles.button,
                  ...styles.buttonPrimary,
                  ...(loading ? styles.buttonDisabled : {}),
                }}
                onClick={() => handleAction('resume')}
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Resume Subscription'}
              </button>
            )}

          {subscription.status === 'past_due' && (
            <button
              style={{
                ...styles.button,
                ...styles.buttonPrimary,
                ...(loading ? styles.buttonDisabled : {}),
              }}
              onClick={() => handleAction('resume')}
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Update Payment Method'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
